// Global Instructions Rule Applied!
//
// Agent activity — the review + oversight tab of the My Agents screen
// (design §6 and the Phase-4.5 live-write addendum).
//
// Three kinds of row land here, and the tab is the single place all of them
// are accountable:
//   * PENDING proposals (propose-only tools): nothing exists until somebody
//     approves. Approve replays the payload through the real write path as the
//     approving human; reject creates nothing.
//   * APPLIED live writes (live-consented tools): the record already exists,
//     created by the agent on the user's behalf. These carry a "Created live"
//     tag and an Undo — the reversibility that replaces pre-approval.
//   * TERMINAL rows — rejected and undone — which stay listed, naming who
//     decided. Since 2026-09-02 an app ADMIN can act on these rows too from
//     Agent Oversight, so "by you" is no longer a safe assumption: every tag
//     names its actor. See utils/proposalPresentation.
// Either way the exact payload is shown verbatim in the expandable row, so
// the user sees what was (or would be) written, not a paraphrase.
//
// REVIEW, NOT BLIND APPROVE (owner 2026-09-02). A pending row's primary verb
// opens the proposal in the New form it is destined for, seeded and editable —
// the payload already IS a New-form record, because every write tool applies
// through the same controller the human form posts to. Approving from there
// creates the record from what is on screen. See ProposalReviewDrawer.
//
// The rendering and the verbs live in utils/proposalPresentation, shared with
// the admin screen: the same event must not read two ways depending on who is
// looking at it.

import React, { useCallback, useMemo, useState } from 'react'
import { Button, Segmented, Table, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useAgentProposals } from '../hooks/useAgentProposals.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { ProposalReviewDrawer } from './ProposalReviewDrawer.tsx'
import {
  ProposalActions,
  ProposalPayloadPanel,
  editedTag,
  outcomeLines,
  statusTag
} from '../utils/proposalPresentation.tsx'
import { payloadsEqual } from '../utils/proposalForms.ts'
import type { AgentProposal } from '../../../state_manager/data_models/agent_access_model.ts'

const { Text } = Typography

// Segmented filter over the feed — pure client-side on status. 'Applied'
// covers both live and approved writes (they share status 'applied');
// 'Other' is the terminal tail (failed / approved-mid-replay / rejected /
// undone).
type ActivityFilter = 'All' | 'Pending' | 'Applied' | 'Other'

const matchesFilter = (proposal: AgentProposal, filter: ActivityFilter): boolean => {
  switch (filter) {
    case 'Pending':
      return proposal.agent_proposals__status === 'pending'
    case 'Applied':
      return proposal.agent_proposals__status === 'applied'
    case 'Other':
      return !['pending', 'applied'].includes(proposal.agent_proposals__status)
    default:
      return true
  }
}

// One stat-strip cell: big number over a small secondary label.
const SummaryStat = ({ value, label }: { value: number; label: string }) => (
  <div
    style={{
      flex: 1,
      minWidth: 140,
      background: '#fafafa',
      borderRadius: 6,
      padding: '8px 12px'
    }}
  >
    <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>
      {value.toLocaleString()}
    </div>
    <Text type='secondary' style={{ fontSize: 12 }}>
      {label}
    </Text>
  </div>
)

export function ProposalsTab() {
  const { proposals, summary, loading, reviewingId, refresh, review, retry, undo } = useAgentProposals()
  const [filter, setFilter] = useState<ActivityFilter>('All')
  const [reviewing, setReviewing] = useState<AgentProposal | null>(null)
  const { openFormInTab } = useTabManagerContext()

  const visibleProposals = useMemo(
    () => proposals.filter((proposal) => matchesFilter(proposal, filter)),
    [proposals, filter]
  )

  const onReview = useCallback((proposal: AgentProposal) => setReviewing(proposal), [])

  // Approve from inside the review form. A failed row goes through retry and a
  // pending one through review — both take the same edited payload, so the
  // correction is persisted identically either way.
  //
  // The payload is sent ONLY when it differs from what the agent filed:
  // an untouched proposal must not acquire an `original_payload` and start
  // reading as amended.
  const handleApprove = useCallback(
    async (payload: Record<string, any>) => {
      if (!reviewing) return
      const filed = (reviewing.agent_proposals__payload ?? {}) as Record<string, unknown>
      const edited = payloadsEqual(payload, filed) ? null : payload
      const ok =
        reviewing.agent_proposals__status === 'failed'
          ? await retry(reviewing.agent_proposals__id, edited)
          : await review(reviewing.agent_proposals__id, 'approve', edited)
      // Close only on a clean round trip. A refused claim (someone else got
      // there first) leaves the form open with the user's edits intact.
      if (ok) setReviewing(null)
    },
    [reviewing, retry, review]
  )

  const handleReject = useCallback(async () => {
    if (!reviewing) return
    const ok = await review(reviewing.agent_proposals__id, 'reject')
    if (ok) setReviewing(null)
  }, [reviewing, review])

  const columns = [
    {
      title: 'Proposal',
      key: 'proposal',
      render: (_: unknown, proposal: AgentProposal) => (
        <>
          <Text strong>
            {proposal.agent_proposals__summary || proposal.agent_proposals__tool}
          </Text>
          {editedTag(proposal)}
          <br />
          <Text type='secondary' style={{ fontSize: 12 }}>
            {proposal.agent_proposals__agent_name} · {proposal.agent_proposals__tool}
          </Text>
          {proposal.agent_proposals__rationale ? (
            <>
              <br />
              <Text type='secondary' style={{ fontSize: 12 }}>
                “{proposal.agent_proposals__rationale}”
              </Text>
            </>
          ) : null}
        </>
      )
    },
    {
      title: 'Filed',
      dataIndex: 'agent_proposals__date_created',
      key: 'filed',
      render: (value: string) => dayjs(value).format('D MMM YYYY')
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, proposal: AgentProposal) => (
        <>
          {statusTag(proposal)}
          {outcomeLines(proposal).map((line, index) => (
            <React.Fragment key={index}>
              <br />
              <Text type={line.type} style={{ fontSize: 12 }}>
                {line.text}
              </Text>
            </React.Fragment>
          ))}
        </>
      )
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, proposal: AgentProposal) => (
        <ProposalActions
          proposal={proposal}
          busy={reviewingId === proposal.agent_proposals__id}
          onReview={onReview as (proposal: AgentProposal) => void}
          onReject={(id) => review(id, 'reject')}
          onUndo={undo}
          onOpen={(target) => openFormInTab(target.module, target.id, null, target.query ?? null)}
        />
      )
    }
  ]

  return (
    <FormSection
      title='Agent activity'
      titleColor='blue'
      loading={loading}
      scrollable
      headerActions={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Segmented
            size='small'
            options={['All', 'Pending', 'Applied', 'Other']}
            value={filter}
            onChange={(value) => setFilter(value as ActivityFilter)}
          />
          <Button size='small' icon={<ReloadOutlined />} onClick={refresh}>
            Refresh
          </Button>
        </div>
      }
    >
      {summary ? (
        // Usage stat strip. Reads/rows are the per-tool daily rollup (UTC
        // day, matching cap enforcement); writes/pending come from the
        // proposal journal below.
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <SummaryStat value={summary.prog__reads_today} label='Reads today (UTC)' />
          <SummaryStat value={summary.prog__rows_returned_today} label='Rows returned today' />
          <SummaryStat value={summary.prog__writes_this_week} label='Writes (7 days)' />
          <SummaryStat value={summary.prog__pending_review} label='Pending review' />
        </div>
      ) : null}
      <Table
        dataSource={visibleProposals}
        columns={columns}
        rowKey='agent_proposals__id'
        pagination={false}
        size='small'
        expandable={{
          expandedRowRender: (proposal: AgentProposal) => (
            <ProposalPayloadPanel proposal={proposal} />
          ),
          rowExpandable: () => true
        }}
        locale={{
          emptyText:
            filter === 'All'
              ? 'Nothing yet. When a connected agent proposes or creates something, it appears here.'
              : 'No entries match this filter.'
        }}
      />

      {/* Rendered here rather than at app level: the drawer is only ever
          reachable from this table, and keeping it local means the reviewed
          proposal and the list's refresh share one owner. */}
      <ProposalReviewDrawer
        proposal={reviewing}
        busy={reviewing ? reviewingId === reviewing.agent_proposals__id : false}
        onApprove={handleApprove}
        onReject={handleReject}
        onClose={() => setReviewing(null)}
        onOpenRecord={openFormInTab}
      />
    </FormSection>
  )
}
